<template>
  <div class="job-board-view">
    <CModal
      id="applyjobModal"
      title="Apply Job"
      color="primary"
      centered:false
      @update:show="onShow"
      :show.sync="successModal"
      >{{ displayMessage }}</CModal
    >
    <div class="view-header">
      <button type="button" class="back-link" @click="goBack">
        <span aria-hidden="true">&larr;</span> Back to Opportunities
      </button>
    </div>
    <div class="skeleton-card" v-if="!(getSelectedJobBoard && getSelectedJobBoard.job_id)"></div>
    <div v-else class="detail-card">
      <job-detail
        ref="detail"
        :getSelectedJobBoard="getSelectedJobBoard"
        @applied="onApplied"
        @hook:mounted="detail = $refs.detail"
        @hook:beforeDestroy="detail = null"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import jobDetail from "@/components/JobBoardDetail/jobDetail.vue";
import { authUrlWithRedirect, jobViewPath } from "@/helpers/helper";

/**
 * Single-job view: just the right-hand detail panel of the job board (jobDetail.vue), without
 * the job-card list. jobDetail's Apply button calls `$parent.onApplyJob(jobId)`, so this page
 * provides the confirm modal + onApplyJob/onWithdrawJob/onShow. For logged-in users the apply /
 * withdraw flow (org registration check, pre-screening questions, applyJob) is jobDetail's own
 * logic, reused through its ref; the modal here just mirrors jobDetail's modal state. Logged-out
 * users are sent to register as on the job board page (JobBoardMixin.js).
 */
export default {
  components: { "job-detail": jobDetail },
  data() {
    return {
      successModal: false,
      modalMessage: "",
      tempJobId: "",
      detail: null,
    };
  },
  computed: {
    ...mapGetters(["getSelectedJobBoard", "getRoleScopes"]),
    displayMessage() {
      return this.getRoleScopes.length
        ? this.detail?.modalMessage
        : this.modalMessage;
    },
  },
  created() {
    const {
      fullPath,
      params: { id },
    } = this.$route;
    this.jobBoardDetailAction({ job_id: id, path: fullPath });
    this.scrollTop();
  },
  methods: {
    ...mapActions(["jobBoardDetailAction", "scrollTop"]),
    goBack() {
      this.$router.push({ path: "/react/dashboard/opportunities" });
    },
    onApplied() {
      // Only when the user came from tf-frontend's Opportunities page (?from=opportunities).
      if (this.$route.query.from !== "opportunities") return;
      this.$router.push({ path: "/react/dashboard/applications" });
    },
    onApplyJob(jobId) {
      this.tempJobId = jobId;
      if (this.getRoleScopes.length) {
        this.detail.onApplyJob();
      } else {
        this.modalMessage = "Please login/Register to apply";
      }
      this.successModal = true;
    },
    onWithdrawJob() {
      this.detail.onWithdrawJob();
      this.successModal = true;
    },
    onShow(e, value) {
      if (this.getRoleScopes.length) {
        this.detail.onShow(e, value);
        return;
      }
      if (value.target.textContent !== " OK ") return;
      // After login/sign-up the candidate lands on this job's page.
      this.$router.push(authUrlWithRedirect(jobViewPath(this.tempJobId))).catch((err) => {
        if (err.name !== "NavigationDuplicated") console.error(err);
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.job-board-view {
  max-width: 1100px;
  margin: 0 auto;
  padding: 20px 16px 32px;
}
.view-header {
  margin-bottom: 12px;
}
.back-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #fff;
  border: 1px solid #e7e7f1;
  border-radius: 999px;
  color: #3f26e7;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, box-shadow 0.15s;
  &:hover {
    background: #eeebfd;
    border-color: #3f26e7;
    box-shadow: 0 2px 8px rgba(63, 38, 231, 0.15);
  }
  &:focus-visible {
    outline: 2px solid #3f26e7;
    outline-offset: 2px;
  }
}
.detail-card {
  background: #fff;
  border: 1px solid #e7e7f1;
  border-radius: 18px;
  box-shadow: 0 6px 24px rgba(63, 38, 231, 0.08);
  overflow: hidden;
  // Restyle the shared job-detail panel for this standalone card (scoped to this page only).
  ::v-deep .job-board-detail-component {
    margin: 0 !important;
    padding: 28px 32px 8px;
    > .container {
      padding: 0;
      max-width: none;
    }
    .logo-summary {
      background: linear-gradient(135deg, #f3f1ff 0%, #eaf8ff 100%);
      border-radius: 14px;
      padding: 20px 12px;
      margin: 0 0 20px !important;
      align-items: center;
    }
    h4,
    h5 {
      font-weight: 700;
    }
    .label {
      font-size: 11px !important;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: #8a8998 !important;
    }
    .value {
      font-size: 15px;
      font-weight: 600;
      color: #14131f;
    }
    .btn.btn-primary {
      min-width: 180px;
      padding: 10px 24px;
      border-radius: 10px;
      font-weight: 600;
      box-shadow: 0 4px 12px rgba(63, 38, 231, 0.3);
    }
    hr {
      display: none;
    }
    img[alt="job-image"] {
      border-radius: 14px;
      object-fit: cover;
      max-height: 220px !important;
    }
  }
}
</style>
